import { colors } from './tokens';
import { FEATURE_ACCENTS } from './featureAccents';

/**
 * Modèle « une couleur d'accent par écran » : chaque écran choisit une clé d'accent, et tous les
 * composants du système (IconPill, FeatureHeader, HowItWorks, ProgressBar, Card mise en avant) en
 * dérivent leur teinte vive (CTA/icône pleine) et leur teinte douce (fond teinté clair).
 *
 * DEUX FAMILLES DE CLÉS, ET LA DISTINCTION COMPTE.
 *
 * Les quatre premières — `blue`, `green`, `lime`, `corail` — sont des accents GÉNÉRIQUES, sans
 * rapport avec une fonctionnalité : réglages, acceptation des CGU, bulles de conversation. Elles ne
 * bougent pas.
 *
 * Les quatre suivantes sont les FEATURES, et elles ne portent aucune couleur en propre : elles
 * lisent `FEATURE_ACCENTS`, la même table que les cartes du carrousel Carrière. C'est ce qui rend
 * l'alignement structurel plutôt que déclaratif — une feature ne PEUT plus diverger de sa carte,
 * puisqu'il n'existe qu'un seul endroit où sa couleur est écrite.
 *
 * Avant cela, chaque écran choisissait son accent à la main et le résultat avait dérivé : la lettre
 * de motivation était rose sur sa carte et ORANGE dans son écran, le CV bleu sur sa carte et violet
 * dans le sien. Personne ne voit les deux côte à côte, donc rien ne le signalait.
 *
 * PIÈGE DE NOMMAGE À CONNAÎTRE : `green` vaut #5CBCE5, un CYAN, et `lime` vaut #A553C7, un VIOLET.
 * Ces noms sont un vestige d'un changement de palette et mentent sur ce qu'ils rendent. C'est la
 * cause principale de la dérive : un écran qui écrivait `accent="lime"` croyait poser du vert.
 * Ne pas s'en servir pour une feature — utiliser sa clé.
 */
export type GenericAccentKey = 'blue' | 'green' | 'lime' | 'corail';
export type FeatureAccentKey = 'cv' | 'linkedin' | 'letter' | 'interview';
export type AccentKey = GenericAccentKey | FeatureAccentKey;

export interface Accent {
  vivid: string;
  soft: string;
}

export const ACCENTS: Record<AccentKey, Accent> = {
  blue: { vivid: colors.bluePrimary, soft: colors.blueSoft },
  green: { vivid: colors.green, soft: colors.greenSoft },
  lime: { vivid: colors.greenLime, soft: colors.limeSoft },
  corail: { vivid: colors.corail, soft: colors.corailSoft },

  // ── Features : dérivées, jamais écrites. `soft` EST le tint de la table, pas une nuance inventée
  // à côté : deux définitions de la même chose finiraient par diverger.
  cv: { vivid: FEATURE_ACCENTS.cv.color, soft: FEATURE_ACCENTS.cv.tint },
  linkedin: { vivid: FEATURE_ACCENTS.linkedin.color, soft: FEATURE_ACCENTS.linkedin.tint },
  letter: { vivid: FEATURE_ACCENTS.letter.color, soft: FEATURE_ACCENTS.letter.tint },
  interview: { vivid: FEATURE_ACCENTS.interview.color, soft: FEATURE_ACCENTS.interview.tint },
};

export const GENERIC_ACCENT_KEYS: GenericAccentKey[] = ['blue', 'green', 'lime', 'corail'];
export const FEATURE_ACCENT_KEYS: FeatureAccentKey[] = ['cv', 'linkedin', 'letter', 'interview'];
export const ACCENT_KEYS: AccentKey[] = [...GENERIC_ACCENT_KEYS, ...FEATURE_ACCENT_KEYS];
